import { FC, useEffect, useMemo, useState } from 'react';
import {
  fetchOrder,
  fetchOrderPreDeliveryChecklists,
  OrderInspectionChecklistsResponse,
  OrderResponse,
  updateOrder,
} from '../../../api/orders';
import { InquiryChecklist } from '../../../api/inquiryChecklists';
import ChecklistYesNo from './checklists/ChecklistYesNo';
import ChecklistMultiSelect from './checklists/ChecklistMultiSelect';
import ChecklistSingleSelect from './checklists/ChecklistSingleSelect';
import ChecklistText from './checklists/ChecklistText';
import { uploadOrderFiles, fetchOrderFiles, deleteOrderFile, type OrderFileRecord } from '../../../api/files';

interface PreDeliveryChecklistPanelProps {
  orderId: string | null;
  showHeader?: boolean;
  onBackToOrders?: () => void;
  onReturnToIntake?: (orderId: string | null) => void;
  onCompleted?: (orderId: string) => void;
  onOrderUpdate?: (order: OrderResponse) => void;
}

const PreDeliveryChecklistPanel: FC<PreDeliveryChecklistPanelProps> = ({
  orderId,
  showHeader = true,
  onBackToOrders,
  onReturnToIntake,
  onCompleted,
  onOrderUpdate,
}) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);

  const [generalChecklists, setGeneralChecklists] = useState<InquiryChecklist[]>([]);
  const [specificChecklists, setSpecificChecklists] = useState<InquiryChecklist[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState<string | null>(null);

  const [responses, setResponses] = useState<Record<string, unknown>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [existingFiles, setExistingFiles] = useState<OrderFileRecord[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const MAX_MB = Number(process.env.REACT_APP_MAX_UPLOAD_MB ?? 25);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setOrderError('Order ID not specified.');
      setLoadingOrder(false);
      return;
    }

    setLoadingOrder(true);
    setOrderError(null);

    fetchOrder(orderId)
      .then((data) => {
        setOrder(data);
      })
      .catch((error) => {
        setOrder(null);
        setOrderError(error instanceof Error ? error.message : 'Failed to load order details.');
      })
      .finally(() => setLoadingOrder(false));
  }, [orderId]);

  useEffect(() => {
    if (!order?.checklists?.length) {
      return;
    }

    const initialResponses: Record<string, unknown> = {};

    order.checklists
      .filter((entry) => entry.type === 'pre_delivery')
      .forEach((entry) => {
        if (entry.checklistId) {
          initialResponses[entry.checklistId] = entry.response ?? null;
        }
      });

    setResponses((prev) => ({ ...initialResponses, ...prev }));
    setFieldErrors((prev) => {
      if (Object.keys(prev).length === 0) {
        return prev;
      }
      const next = { ...prev };
      order.checklists
        .filter((entry) => entry.type === 'pre_delivery')
        .forEach((entry) => {
          if (entry.checklistId && next[entry.checklistId]) {
            delete next[entry.checklistId];
          }
        });
      return next;
    });
  }, [order?.checklists]);

  useEffect(() => {
    if (!orderId) {
      setGeneralChecklists([]);
      setSpecificChecklists([]);
      return;
    }

    setChecklistLoading(true);
    setChecklistError(null);

    fetchOrderPreDeliveryChecklists(orderId)
      .then((response: OrderInspectionChecklistsResponse) => {
        setGeneralChecklists(response.general ?? []);
        setSpecificChecklists(response.specific ?? []);
      })
      .catch((error) => {
        setChecklistError(error instanceof Error ? error.message : 'Failed to load pre-delivery checklists.');
        setGeneralChecklists([]);
        setSpecificChecklists([]);
      })
      .finally(() => setChecklistLoading(false));
  }, [orderId]);

  const clearFieldError = (id: string) => {
    setFieldErrors((prev) => {
      if (!prev[id]) {
        return prev;
      }
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleBooleanChange = (id: string, value: boolean) => {
    setResponses((prev) => ({ ...prev, [id]: value }));
    clearFieldError(id);
  };

  const handleTextChange = (id: string, value: string) => {
    setResponses((prev) => ({ ...prev, [id]: value }));
    clearFieldError(id);
  };

  const handleMultiSelectChange = (id: string, value: string[]) => {
    setResponses((prev) => ({ ...prev, [id]: value }));
    clearFieldError(id);
  };

  const handleClearResponse = (id: string) => {
    setResponses((prev) => {
      const next = { ...prev };
      next[id] = null;
      return next;
    });
    clearFieldError(id);
  };

  const brandName = useMemo(() => order?.customerVehicle?.vehicleBrand?.name ?? '', [order]);
  const modelName = useMemo(() => order?.customerVehicle?.vehicleModel?.name ?? '', [order]);
  const allChecklists = useMemo(
    () => [...generalChecklists, ...specificChecklists],
    [generalChecklists, specificChecklists],
  );
  const mandatoryChecklists = useMemo(
    () => allChecklists.filter((c) => c.isMandatory),
    [allChecklists],
  );

  const checklistAnswered = (checklist: InquiryChecklist) => {
    const value = responses[checklist.id];

    switch (checklist.inputType) {
      case 'boolean':
        return typeof value === 'boolean';
      case 'multi_select':
        return Array.isArray(value) && value.length > 0;
      case 'single_select':
        return typeof value === 'string' && value.trim().length > 0;
      case 'text':
      default:
        return typeof value === 'string' && value.trim().length > 0;
    }
  };

  const hasAnyResponses = useMemo(() => {
    return Object.values(responses).some((value) => {
      if (value === null || value === undefined) {
        return false;
      }
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (typeof value === 'string') {
        return value.trim().length > 0;
      }
      return true;
    });
  }, [responses]);

  const hasUnanswered = mandatoryChecklists.some((checklist) => !checklistAnswered(checklist));

  const validateResponses = () => {
    const errors: Record<string, string> = {};

    mandatoryChecklists.forEach((checklist) => {
      if (!checklistAnswered(checklist)) {
        errors[checklist.id] = 'Answer this checklist before continuing.';
      }
    });

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!orderId) {
      return;
    }

    // If some answers exist, enforce validation. If none are selected,
    // allow the user to skip without forcing mandatory checklists.
    if (hasAnyResponses && !validateResponses()) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload = allChecklists.map((checklist, index) => ({
      checklistId: checklist.id,
      type: checklist.type,
      response: responses[checklist.id],
      sortOrder: index + 1,
    }));

    try {
      const updated = await updateOrder(orderId, {
        checklists: payload,
      });

      setOrder(updated);
      onOrderUpdate?.(updated);

      const updatedResponses: Record<string, unknown> = {};
      updated.checklists
        .filter((entry) => entry.type === 'pre_delivery')
        .forEach((entry) => {
          if (entry.checklistId) {
            updatedResponses[entry.checklistId] = entry.response ?? null;
          }
        });
      setResponses(updatedResponses);
      setFieldErrors({});

      if (onCompleted) {
        onCompleted(updated.id);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Failed to save pre-delivery responses.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    if (!orderId) {
      return;
    }
    if (hasAnyResponses) {
      return;
    }
    if (onCompleted) {
      onCompleted(orderId);
    }
  };

  const handleUpload = async () => {
    if (!orderId || !selectedFiles || selectedFiles.length === 0) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      await uploadOrderFiles({ orderId, type: 'delivery', files: selectedFiles });
      setUploadMsg('Files uploaded');
      setSelectedFiles([]);
      const latest = await fetchOrderFiles(orderId, 'delivery');
      setExistingFiles(latest);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setUploadMsg(msg);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!orderId) {
      setExistingFiles([]);
      return;
    }
    setFilesLoading(true);
    setFilesError(null);
    fetchOrderFiles(orderId, 'delivery')
      .then(setExistingFiles)
      .catch((e) => setFilesError(e instanceof Error ? e.message : 'Failed to load files'))
      .finally(() => setFilesLoading(false));
  }, [orderId, uploading]);

  const renderChecklistCard = (checklist: InquiryChecklist) => {
    const value = responses[checklist.id];
    const label = checklist.name;

    switch (checklist.inputType) {
      case 'boolean':
        return (
          <ChecklistYesNo
            key={checklist.id}
            label={label}
            isRequired={checklist.isMandatory}
            value={typeof value === 'boolean' ? value : null}
            onChange={(next) => handleBooleanChange(checklist.id, next)}
            onClear={() => handleClearResponse(checklist.id)}
            helperText={fieldErrors[checklist.id] ?? undefined}
          />
        );
      case 'multi_select':
        return (
          <ChecklistMultiSelect
            key={checklist.id}
            label={label}
            isRequired={checklist.isMandatory}
            options={checklist.answerOptions ?? []}
            value={Array.isArray(value) ? (value as string[]) : []}
            onChange={(next) => handleMultiSelectChange(checklist.id, next)}
            onClear={() => handleClearResponse(checklist.id)}
            helperText={fieldErrors[checklist.id] ?? undefined}
          />
        );
      case 'single_select':
        return (
          <ChecklistSingleSelect
            key={checklist.id}
            label={label}
            isRequired={checklist.isMandatory}
            options={checklist.answerOptions ?? []}
            value={typeof value === 'string' ? (value as string) : null}
            onChange={(next) => handleTextChange(checklist.id, next)}
            onClear={() => handleClearResponse(checklist.id)}
            helperText={fieldErrors[checklist.id] ?? undefined}
          />
        );
      case 'text':
      default:
        return (
          <ChecklistText
            key={checklist.id}
            label={label}
            isRequired={checklist.isMandatory}
            value={typeof value === 'string' ? (value as string) : ''}
            onChange={(next) => handleTextChange(checklist.id, next)}
            onClear={() => handleClearResponse(checklist.id)}
            helperText={fieldErrors[checklist.id] ?? undefined}
          />
        );
    }
  };

  return (
    <div className="space-y-6">
      {showHeader ? (
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-slate-800">Pre-delivery checklist</h1>
          <p className="text-sm text-slate-500">
            Review the pre-delivery checklist for order <span className="font-semibold">{orderId ?? '—'}</span>. Provide
            responses for each item before moving on.
          </p>
          {brandName || modelName ? (
            <p className="text-xs text-slate-400">
              Vehicle: {[brandName, modelName].filter(Boolean).join(' · ') || 'Unspecified'}
            </p>
          ) : null}
        </div>
      ) : null}

      {orderError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger">{orderError}</div>
      ) : null}

      {loadingOrder ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 my-2">
          Loading order information…
        </div>
      ) : null}

      {!loadingOrder && checklistError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 p-4 text-sm text-danger">{checklistError}</div>
      ) : null}

      {!loadingOrder && !checklistLoading ? (
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Pre-delivery photos</h2>
            <div className="mt-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50/50 p-3">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={(e) => {
                    const list = e.target.files ? Array.from(e.target.files) : [];
                    const maxBytes = MAX_MB * 1024 * 1024;
                    const kept = list.filter((f) => f.size <= maxBytes);
                    const dropped = list.length - kept.length;
                    setSelectedFiles(kept.length ? kept : null);
                    if (dropped > 0) {
                      setUploadMsg(`Some files were too large and skipped (max ${MAX_MB}MB each).`);
                    } else {
                      setUploadMsg(null);
                    }
                  }}
                  disabled={!orderId || uploading}
                />
                <span className="text-[11px] text-slate-500">Images/Videos up to {MAX_MB}MB each</span>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary disabled:opacity-60"
                  onClick={handleUpload}
                  disabled={!orderId || !selectedFiles || selectedFiles.length === 0 || uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload selected'}
                </button>
                {uploadMsg ? <span className="text-xs text-slate-500">{uploadMsg}</span> : null}
              </div>
              {selectedFiles && selectedFiles.length > 0 ? (
                <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
                  {selectedFiles.map((f, idx) => {
                    const isVideo = (f.type || '').startsWith('video/');
                    const src = URL.createObjectURL(f);
                    return (
                      <div key={`${f.name}-${idx}`} className="rounded-lg border border-slate-200 p-1 bg-white">
                        {isVideo ? (
                          <video src={src} className="aspect-square w-full object-cover rounded" muted playsInline />
                        ) : (
                          <img src={src} alt={f.name} className="aspect-square w-full object-cover rounded" />
                        )}
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <div className="truncate text-xs text-slate-500" title={f.name}>{f.name}</div>
                          <button
                            type="button"
                            className="text-[11px] text-danger hover:underline"
                            onClick={() => setSelectedFiles((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev))}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Uploaded</p>
          {filesLoading ? (
            <div className="text-xs text-slate-500 mt-1">Loading…</div>
          ) : filesError ? (
            <div className="text-xs text-danger mt-1">{filesError}</div>
          ) : existingFiles.length === 0 ? (
            <div className="text-xs text-slate-400 mt-1">No files yet.</div>
          ) : (
            <div className="mt-2 grid grid-cols-4 md:grid-cols-6 gap-2">
              {existingFiles.map((f) => {
                const isVideo = (f.contentType || '').startsWith('video/');
                return (
                <div key={f.id} className="rounded-lg border border-slate-200 p-1 bg-white">
                  <button type="button" onClick={() => window.open(f.url, '_blank')} className="w-full text-left">
                    {isVideo ? (
                      <video src={f.url} className="aspect-square w-full object-cover rounded" muted playsInline />
                    ) : (
                      <img src={f.url} alt={f.originalName ?? ''} className="aspect-square w-full object-cover rounded" />
                    )}
                  </button>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <div className="truncate text-xs text-slate-500" title={f.originalName ?? ''}>{f.originalName ?? ''}</div>
                    <button
                      className="text-[11px] text-danger hover:underline disabled:opacity-50"
                      onClick={async () => {
                        if (!orderId) return;
                        if (!window.confirm('Delete this file?')) return;
                        setDeletingId(f.id);
                        try {
                          await deleteOrderFile(orderId, f.id, f.s3Key);
                          setExistingFiles((prev) => prev.filter((x) => x.id !== f.id));
                          setUploadMsg('Deleted');
                        } catch (e) {
                          setUploadMsg(e instanceof Error ? e.message : 'Delete failed');
                        } finally {
                          setDeletingId(null);
                        }
                      }}
                      disabled={deletingId === f.id}
                    >
                      {deletingId === f.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </div>
                </div>
              );
              })}
            </div>
          )}
        </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">General pre-delivery checks</h2>
            <div className="mt-3 grid grid-cols-3 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {generalChecklists.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 md:col-span-2 xl:col-span-3">
                  No general pre-delivery checklists configured yet.
                </div>
              ) : (
                generalChecklists.map((checklist) => renderChecklistCard(checklist))
              )}
            </div>
          </section>

          <section className='my-2'>
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">Vehicle-specific pre-delivery checks</h2>
            <div className="mt-3  grid-cols-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {specificChecklists.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 p-4 text-xs text-slate-500 md:col-span-2 xl:col-span-3">
                  No brand or model specific pre-delivery checklists for this vehicle yet.
                </div>
              ) : (
                specificChecklists.map((checklist) => renderChecklistCard(checklist))
              )}
            </div>
          </section>

          <section>
            <p className="mt-1 text-[11px] leading-snug text-slate-500 italic">
              Disclaimer: Pre-delivery checks are visual/operational confirmations only and do not guarantee the
              absence of hidden defects. The workshop is not liable for issues that are not reasonably detectable at the
              time of handover.
            </p>
          </section>
        </div>
      ) : null}

      {checklistLoading ? (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 my-2">
          Loading pre-delivery checklists…
        </div>
      ) : null}

      {saveError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 p-3 text-sm text-danger">{saveError}</div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {(onBackToOrders || onReturnToIntake) && (
          <>
            {onBackToOrders ? (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90"
                onClick={onBackToOrders}
              >
                Back to order management
              </button>
            ) : null}
            {onReturnToIntake ? (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary"
                onClick={() => onReturnToIntake(orderId ?? null)}
              >
                Return to intake
              </button>
            ) : null}
          </>
        )}
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSkip}
          disabled={!orderId || hasAnyResponses || saving}
        >
          Skip checklist &amp; continue
        </button>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-primary/60"
          onClick={handleSave}
          disabled={!orderId || (hasUnanswered && hasAnyResponses) || saving}
        >
          {saving ? 'Saving…' : 'Save & continue'}
        </button>
      </div>
    </div>
  );
};

export default PreDeliveryChecklistPanel;
